// client/src/pages/seo/FreeDigitalMenuForRestaurantsEn.jsx
import React, { useEffect, useMemo, useState } from "react";
import SeoRelatedLinks from "../../components/seo/SeoRelatedLinks.jsx";
import SeoHeader from "../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "check",
    label: "Is a free digital menu enough for your restaurant?",
  },
  {
    id: "definition",
    label: "What is a free digital menu?",
  },
  {
    id: "features",
    label: "What should a free digital menu include?",
  },
  {
    id: "why-free",
    label: "Why is a free start useful for restaurants?",
  },
  {
    id: "enough",
    label: "When is a free plan enough?",
  },
  {
    id: "upgrade",
    label: "When is the free version not enough anymore?",
  },
  {
    id: "pdf",
    label: "Free QR menu or PDF behind a QR code?",
  },
  {
    id: "sales",
    label: "Can a free digital menu help with sales?",
  },
  {
    id: "test",
    label: "How to test a free digital menu in 30 minutes",
  },
  {
    id: "bestplace",
    label: "How Best Place helps you start for free",
  },
  {
    id: "faq",
    label: "FAQ about free digital menus",
  },
];

const checklist = [
  "You want to test a QR menu first.",
  "Your restaurant has a small or manageable menu.",
  "A simple start without complex design is enough for now.",
  "You want to see whether guests use the QR code.",
  "You want to change names, prices, or availability faster.",
  "You are not sure yet whether you need a full restaurant software system.",
  "You do not want to pay for a new tool immediately.",
  "A basic online menu without complex automation is enough for the first test.",
  "You want to test the digital menu in one location first.",
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function getResult(score) {
  if (score <= 3) {
    return {
      title: "A free version may be too basic",
      text:
        "If you immediately need reservations, advanced design, your own domain, multiple locations, or deeper automation, a free plan may not be enough for long.",
      tone: "low",
    };
  }

  if (score <= 6) {
    return {
      title: "A free test makes sense",
      text:
        "For your restaurant, a free digital menu can be a good first step: test the QR code, manage a basic menu, check the smartphone view, and watch guest reactions.",
      tone: "medium",
    };
  }

  return {
    title: "A free plan is a strong starting point",
    text:
      "Your restaurant can probably test without much risk whether a digital menu helps in daily work: with prices, photos, availability, QR access, and faster updates.",
    tone: "high",
  };
}

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Visual idea">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>
      <div>
        <strong>Idea for illustration:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function FreeDigitalMenuForRestaurantsEn() {
  const [checked, setChecked] = useState([]);

  const score = checked.length;
  const result = useMemo(() => getResult(score), [score]);

  useEffect(() => {
    document.documentElement.setAttribute("lang", "en");
    document.title =
      "Free Digital Menu for Restaurants: Test First, Invest Later | Best Place";
  }, []);

  const toggle = (item) => {
    setChecked((prev) =>
      prev.includes(item) ? prev.filter((x) => x !== item) : [...prev, item]
    );
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="en"
        langLinks={{
          en: "/en/free-digital-menu-for-restaurants",
          de: "/de/kostenlose-digitale-speisekarte",
          no: "/no/gratis-digital-meny-for-restauranter",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Free digital menu for restaurants: test first, invest later
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                A free digital menu is not a cheap replacement for a real
                system. It is a safe first step to test whether a QR code menu
                can help in your restaurant’s daily work.
              </p>

              <p className="seoHero__text">
                Not every restaurant needs a large software package immediately.
                But almost every restaurant can test whether a digital menu
                looks good on smartphones, whether guests understand the QR code,
                and whether updates become easier.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="en" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Content of the article</h2>

        <nav className="qrArticle__nav" aria-label="Article navigation">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="check" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Is a free digital menu enough for your restaurant?</h2>

            <p>
              Press the statements that sound true for your restaurant. The result is
              intentionally honest: not every restaurant needs a complex digital setup
              from day one, but many restaurants can benefit from a simple QR menu much
              earlier than they think.
            </p>

            <div className="qrArticle__checkGrid">
              {checklist.map((item) => (
                <label key={item} className="qrArticle__checkItem">
                  <input
                    type="checkbox"
                    checked={checked.includes(item)}
                    onChange={() => toggle(item)}
                  />
                  <span className="text">{item}</span>
                </label>
              ))}
            </div>

            <div className={`qrArticle__result qrArticle__result--${result.tone}`}>
              <div className="qrArticle__score">
                {score} / {checklist.length}
              </div>

              <div>
                <strong>{result.title}</strong>
                <p>{result.text}</p>
              </div>
            </div>
          </div>
        </section>

        <section id="definition" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>What is a free digital menu?</h2>

            <p>
              A free digital menu is an online menu that a restaurant can create
              and test without initial costs. Guests usually open it through a
              QR code on the table or through a link on the restaurant website.
            </p>

            <p>
              Free should not mean unprofessional. The purpose of a free version
              is not to solve every problem immediately. Its purpose is to give
              the restaurant a safe way to test the real process.
            </p>

            <h3>Test the real workflow first</h3>

            <p>
              A free plan means: test the real workflow first, then decide what
              is worth expanding. It gives the restaurant a practical way to see
              whether a digital menu fits daily work before choosing a bigger
              system.
            </p>
          </div>
        </section>

        <section id="features" className="acquaintance">
          <h2>What should a free digital menu include?</h2>

          <p>
            A free version does not need every feature. But it should provide
            enough value to let the restaurant test whether the idea works in
            real life.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Create a menu.</li>
            <li className="text__seo-li">Add categories.</li>
            <li className="text__seo-li">Add dish names.</li>
            <li className="text__seo-li">Enter prices.</li>
            <li className="text__seo-li">Add descriptions.</li>
            <li className="text__seo-li">Test photos.</li>
            <li className="text__seo-li">Use a QR code.</li>
            <li className="text__seo-li">Check mobile view.</li>
            <li className="text__seo-li">Edit items.</li>
            <li className="text__seo-li">Hide dishes.</li>
            <li className="text__seo-li">Share a basic link.</li>
            <li className="text__seo-li">Run a team test.</li>
          </ul>

          <p>
            If you can create a menu yourself, change a price, and open it on a
            smartphone, the free version already has real practical value.
          </p>

          <VisualIdea>
            A simple free menu test: one category, a few dishes, prices,
            descriptions, a QR code and a smartphone view.
          </VisualIdea>
        </section>

        <section id="why-free" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Why is a free start useful for restaurants?</h2>

            <p>
              Restaurants rarely introduce new tools blindly. It is not only
              about money. It is also about time, staff habits, guest behavior,
              and the question of whether people will actually use the new tool.
            </p>

            <p>
              That is why a small test is often smarter than a big immediate
              change. A restaurant can start with a few products, test the QR
              code on one table, and see whether the process is clear.
            </p>

            <h3>A free test reduces risk</h3>

            <p>
              A free digital menu allows the restaurant to check the idea before
              making a larger decision. The owner can see whether updates become
              easier, whether the QR code is understood and whether the team can
              work with the menu.
            </p>
          </div>
        </section>

        <section id="enough" className="acquaintance">
          <h2>When is a free plan enough?</h2>

          <p>
            A free plan can be enough when you want to test practical value
            first and do not yet need complex features.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Your menu is manageable.</li>
            <li className="text__seo-li">You first need QR access only.</li>
            <li className="text__seo-li">You want to test guest reactions.</li>
            <li className="text__seo-li">
              You do not need highly custom design yet.
            </li>
            <li className="text__seo-li">You have one location.</li>
            <li className="text__seo-li">
              You want to update prices and availability faster.
            </li>
          </ul>

          <h3>Example</h3>

          <p>
            A café with 25 items wants to test QR codes on a few tables: coffee,
            desserts, breakfast, and seasonal drinks. For this kind of start, a
            free plan can be completely enough.
          </p>
        </section>

        <section id="upgrade" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>When is the free version not enough anymore?</h2>

            <p>
              A free version is a starting point. It is not always the final
              solution. Once the digital menu becomes part of your daily
              operations, advanced features can become useful.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Free plan is often enough for</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">First test.</li>
                  <li className="text__seo-li">Small menu.</li>
                  <li className="text__seo-li">Trying QR access.</li>
                  <li className="text__seo-li">Basic smartphone view.</li>
                  <li className="text__seo-li">First photos and prices.</li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Upgrade becomes useful for</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">Your own subdomain link.</li>
                  <li className="text__seo-li">More design control.</li>
                  <li className="text__seo-li">Online reservations.</li>
                  <li className="text__seo-li">Multilingual menu.</li>
                  <li className="text__seo-li">Promotion areas.</li>
                  <li className="text__seo-li">
                    More control over your public presence.
                  </li>
                </ul>
              </div>
            </div>

            <h3>Validate first, then expand</h3>

            <p>
              Starting free does not mean staying free forever. It means:
              validate first, then expand with purpose.
            </p>
          </div>
        </section>

        <section id="pdf" className="acquaintance">
          <h2>Free QR menu or PDF behind a QR code?</h2>

          <p>
            Many restaurants start with a PDF behind a QR code. This is better
            than nothing, but it is not always the best experience for guests on
            smartphones.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>PDF behind QR code</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Quick to create.</li>
                <li className="text__seo-li">Often free.</li>
                <li className="text__seo-li">
                  Less comfortable on smartphones.
                </li>
                <li className="text__seo-li">Harder to update.</li>
                <li className="text__seo-li">
                  Weak handling of sold-out items.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Digital menu</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Mobile-friendly.</li>
                <li className="text__seo-li">Clear categories.</li>
                <li className="text__seo-li">Easier to update.</li>
                <li className="text__seo-li">Photos and descriptions.</li>
                <li className="text__seo-li">Items can be hidden.</li>
                <li className="text__seo-li">
                  CTA to demo or reservation possible.
                </li>
              </ul>
            </div>
          </div>

          <p>
            With a QR code, the code itself is not the value. The real value is
            the experience after scanning: readability, speed, structure and
            useful information.
          </p>
        </section>

        <section id="sales" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Can a free digital menu help with sales?</h2>

            <p>
              A digital menu does not guarantee higher sales. But it can make
              offers more visible: starters, desserts, drinks, sets, daily
              specials, or seasonal recommendations.
            </p>

            <p>
              The point is not aggressive upselling. The point is better
              visibility. A guest can only order what they notice, understand,
              and find appealing at the right moment.
            </p>

            <h3>Visibility is part of the menu experience</h3>

            <p>
              A simple digital menu can already help show the right items more
              clearly: recommendations, seasonal offers, drinks, desserts or
              limited-time products.
            </p>
          </div>
        </section>

        <section id="test" className="acquaintance">
          <h2>How to test a free digital menu in 30 minutes</h2>

          <p>
            The first test does not need to be perfect. It only needs to answer
            three questions: Can you edit the menu yourself? Does the guest
            understand the flow? Is it worth continuing?
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">Select 5–10 popular items.</li>
            <li className="text__seo-li">
              Add names, prices, and short descriptions.
            </li>
            <li className="text__seo-li">Add 2–3 photos.</li>
            <li className="text__seo-li">Generate a QR code.</li>
            <li className="text__seo-li">
              Open the menu on your smartphone.
            </li>
            <li className="text__seo-li">Show it to one team member.</li>
            <li className="text__seo-li">
              Test the QR code at a real table.
            </li>
            <li className="text__seo-li">
              Check whether the next action is clear.
            </li>
            <li className="text__seo-li">
              Try changing a price or hiding an item.
            </li>
          </ol>

          <VisualIdea>
            A 30-minute test flow: add a few real products, open the QR code on
            a smartphone, show it to the team and make one small menu update.
          </VisualIdea>
        </section>

        <div id="bestplace" className="qrArticle__anchorTarget">
          <SeoWorkSteps lang="en" />
        </div>

        <section id="faq" className="acquaintance">
          <h2>Frequently asked questions about free digital menus</h2>

          <div className="seoFaq">
            <details>
              <summary>Can a digital menu really be free?</summary>
              <p>
                Yes, a basic version can be free if the provider offers a free
                plan for simple menus and QR code access.
              </p>
            </details>

            <details>
              <summary>What is the catch with a free version?</summary>
              <p>
                There should not be a hidden catch. But there are usually
                limits: fewer design options, less automation, or no advanced
                features such as reservations.
              </p>
            </details>

            <details>
              <summary>Is a PDF with a QR code enough?</summary>
              <p>
                For a very simple start, a PDF can be enough. For daily
                smartphone use, a mobile digital menu is usually more
                comfortable.
              </p>
            </details>

            <details>
              <summary>When should I switch to a paid version?</summary>
              <p>
                When you need more control, your own subdomain, reservations,
                more design, languages, or marketing features.
              </p>
            </details>

            <details>
              <summary>Can I start without a developer?</summary>
              <p>
                Yes. The idea of this kind of tool is that owners or
                administrators can manage products, prices, photos, and
                descriptions themselves.
              </p>
            </details>

            <details>
              <summary>Is this useful for small restaurants?</summary>
              <p>
                Yes. Small restaurants often benefit from being able to make
                menu changes without starting a new design or printing process
                every time.
              </p>
            </details>
          </div>
        </section>
        <SeoRelatedLinks
          lang="en"
          group="menu"
          exclude="/en/free-digital-menu-for-restaurants"
        />
      </article>
    </main>
  );
}